import { useCreateWhiteningOrder } from '@/api/hooks/useCreateWhiteningOrder'
import { useFetchWhiteningSummary } from '@/api/hooks/useFetchWhiteningSummary'
import type { WhiteningOrderKind } from '@/api/types/PatientApi'
import { ErrorState } from '@/components/ErrorState'
import ProtectedPage from '@/components/ProtectedPage'
import { GelCard } from '@/components/WhiteningPage/GelCard'
import { KitCard } from '@/components/WhiteningPage/KitCard'
import { ShipToSheet } from '@/components/WhiteningPage/ShipToSheet'
import { isApiRequestError } from '@/errors/ApiRequestError'
import {
	type WhiteningActionId,
	buildWhiteningView,
	shipToFor,
} from '@/features/whitening/state'
import { analytics } from '@/lib/analytics'
import { FlowHeader } from '@/ui/core/flow-header'
import { PageShell } from '@/ui/core/page-shell'
import { Screen } from '@/ui/core/screen'
import { Sheet } from '@/ui/core/sheet'
import { Skeleton } from '@/ui/core/skeleton'
import { VStack } from '@/ui/core/stack'
import { router } from 'expo-router'
import { useState } from 'react'
import { View } from 'react-native'
import Toast from 'react-native-toast-message'

const goBack = () => (router.canGoBack() ? router.back() : router.replace('/'))

/** The air between the two cards. */
const BLOCK = 20

/** The member cannot be shipped anything until we know where. */
const SHIPPING_ADDRESS_MISSING = 'SHIPPING_ADDRESS_MISSING'

/**
 * A skeleton in the shape of the kit card, so nothing jumps when the summary lands.
 *
 * The measurements are the card's own and are written as points rather than as Tailwind steps:
 * this rem is fourteen, so `p-3` is not twelve, and a shape meant to stand exactly where the
 * real one will has to be able to say twelve.
 */
const KitLoading = () => (
	<View className="rounded-note bg-white" style={{ padding: 12 }}>
		{/* Skeletons take no style of their own, so their measurements are arbitrary classes —
		    exact points either way, which is what matters here. */}
		<Skeleton isLoading className="h-[200px] w-full rounded-brand bg-blush" />
		<VStack gap={12} style={{ padding: 14, paddingTop: 24 }}>
			<Skeleton isLoading className="h-[56px] w-full bg-blush" />
			<Skeleton isLoading className="h-[56px] w-full bg-blush" />
			<Skeleton isLoading className="mt-2 h-[46px] w-full bg-blush" />
		</VStack>
	</View>
)

/**
 * The whitening kit's own screen: cards, and nothing between them.
 *
 * Everything a state needs lives inside the card it belongs to — the parcel, the claim's date,
 * the button, the last line. There is no page-level anything, which is what lets the page hold
 * two subjects at once: a delivered kit whose tracking number is now a receipt, and a refill in
 * a van. One page-level slot could only ever describe the newer of the two.
 *
 * The server decides which states those are. The app holds no claim flag of its own any more.
 */
export default function WhiteningScreen() {
	const { summary, isLoading, isError, refetch } = useFetchWhiteningSummary()
	const { mutate: createOrder, isPending } = useCreateWhiteningOrder()
	// The one sheet this page has: the confirmation for a kind of order.
	const [ordering, setOrdering] = useState<WhiteningOrderKind | null>(null)

	const view = buildWhiteningView({ summary })

	const onConfirm = () => {
		if (!ordering) return
		analytics.trackClick(
			ordering === 'KIT' ? 'whitening-kit-claim' : 'whitening-gel-request'
		)
		createOrder(
			{ kind: ordering },
			{
				onSuccess: () => {
					setOrdering(null)
					Toast.show({
						type: 'success',
						text1:
							ordering === 'KIT' ? 'Your kit is claimed' : 'Your refill is on its way',
					})
				},
				onError: (error) => {
					// The one refusal the member can do something about, so it takes them there
					// instead of telling them what went wrong and leaving them on this sheet.
					if (isApiRequestError(error) && error.code === SHIPPING_ADDRESS_MISSING) {
						setOrdering(null)
						Toast.show({ type: 'error', text1: 'We need a shipping address first' })
						router.navigate({ pathname: '/profile', params: { sheet: 'address' } })
						return
					}
					Toast.show({
						type: 'error',
						text1: error.message || 'We could not place that order, please try again',
					})
				},
			}
		)
	}

	// A card's button opens the confirmation; it no longer orders anything itself.
	const onAction = (action: WhiteningActionId) =>
		setOrdering(action === 'claim-kit' ? 'KIT' : 'GEL')

	return (
		<ProtectedPage>
			<Screen preset="fixed" className="flex flex-1" safeAreaEdges={['top']}>
				{/* No rule here: the kit card starts directly under the chrome and its own top edge
				    is the floor. A hairline plus a card edge three points below it read as two
				    lines rather than as a header and a card. */}
				<FlowHeader onBack={goBack} rule={false} />
				{/* And less air above it than a page of type would take, for the same reason: the
				    card carries its own inset, so the shell's eight points of padding put the
				    card's corner a third of a line below where the eye expects the page to start. */}
				<PageShell contentClassName="pt-3">
					<VStack gap={BLOCK}>
						{isLoading ? (
							<KitLoading />
						) : (
							<>
								<KitCard
									card={view.kit}
									onAction={onAction}
									onMessageUs={() => router.navigate('/chat')}
								/>
								{/* Both cards, in every state. The refill's is a heads-up before there is
							    anything to refill: it cannot act then, but it is where a member
							    learns that refills exist and are paced, which is better met early
							    than on the morning the gel runs out. */}
								<GelCard card={view.gel} onAction={onAction} />
							</>
						)}

						{isError && (
							<ErrorState
								title="We couldn't load your kit"
								line="Check your connection and try again."
								onAction={() => refetch()}
							/>
						)}
					</VStack>
				</PageShell>
			</Screen>

			<Sheet open={!!ordering} onClose={() => setOrdering(null)}>
				{/* `ordering` is read once here rather than inside: the sheet stays mounted for a
				    beat as it slides out, and a null kind mid-animation would blank its button. */}
				<ShipToSheet
					kind={ordering ?? 'KIT'}
					shipTo={shipToFor(summary)}
					scanOnFile={!!summary?.scanOnFile}
					isBusy={isPending}
					onConfirm={onConfirm}
					onClose={() => setOrdering(null)}
				/>
			</Sheet>
		</ProtectedPage>
	)
}
